/**
 * Image Slider Section
 * Vanilla JS, scoped per-section instance. No external dependencies,
 * so it will not conflict with theme JS or other sliders on the page.
 */
(function () {
  'use strict';

  function initSlider(root) {
    var track = root.querySelector('.image-slider__track');
    if (!track) return;

    var slides = Array.prototype.slice.call(track.children);
    if (slides.length === 0) return;

    var prevBtn = root.querySelector('[data-slider-prev]');
    var nextBtn = root.querySelector('[data-slider-next]');
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-slider-dot]'));

    var loop = root.getAttribute('data-loop') === 'true';
    var autoplay = root.getAttribute('data-autoplay') === 'true';
    var autoplaySpeed = parseInt(root.getAttribute('data-autoplay-speed'), 10) || 5000;

    var currentIndex = 0;
    var slideCount = slides.length;
    var autoplayTimer = null;
    var isPointerDown = false;
    var pointerStartX = 0;
    var pointerDeltaX = 0;

    function goTo(index, skipTransition) {
      if (loop) {
        currentIndex = (index + slideCount) % slideCount;
      } else {
        currentIndex = Math.max(0, Math.min(index, slideCount - 1));
      }

      if (skipTransition) {
        track.style.transition = 'none';
      } else {
        track.style.transition = '';
      }

      track.style.transform = 'translateX(-' + (currentIndex * 100) + '%)';

      if (skipTransition) {
        // Force reflow so the next transition re-applies
        void track.offsetHeight;
        track.style.transition = '';
      }

      updateDots();
      updateArrows();
    }

    function updateDots() {
      dots.forEach(function (dot, i) {
        var active = i === currentIndex;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
      });
    }

    function updateArrows() {
      if (!loop && prevBtn) prevBtn.disabled = currentIndex === 0;
      if (!loop && nextBtn) nextBtn.disabled = currentIndex === slideCount - 1;
    }

    function next() {
      goTo(currentIndex + 1);
    }

    function prev() {
      goTo(currentIndex - 1);
    }

    function startAutoplay() {
      if (!autoplay || slideCount <= 1) return;
      stopAutoplay();
      autoplayTimer = setInterval(function () {
        if (!loop && currentIndex === slideCount - 1) {
          goTo(0);
        } else {
          next();
        }
      }, autoplaySpeed);
    }

    function stopAutoplay() {
      if (autoplayTimer) {
        clearInterval(autoplayTimer);
        autoplayTimer = null;
      }
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        next();
        restartAutoplay();
      });
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        prev();
        restartAutoplay();
      });
    }

    dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        var idx = parseInt(dot.getAttribute('data-index'), 10);
        goTo(idx);
        restartAutoplay();
      });
    });

    function restartAutoplay() {
      stopAutoplay();
      startAutoplay();
    }

    // Pause on hover / focus for accessibility and UX
    root.addEventListener('mouseenter', stopAutoplay);
    root.addEventListener('mouseleave', startAutoplay);
    root.addEventListener('focusin', stopAutoplay);
    root.addEventListener('focusout', startAutoplay);

    // Touch / pointer swipe support
    track.addEventListener('pointerdown', function (e) {
      isPointerDown = true;
      pointerStartX = e.clientX;
      pointerDeltaX = 0;
      track.style.transition = 'none';
      stopAutoplay();
    });

    track.addEventListener('pointermove', function (e) {
      if (!isPointerDown) return;
      pointerDeltaX = e.clientX - pointerStartX;
      var percentDelta = (pointerDeltaX / track.clientWidth) * 100;
      track.style.transform = 'translateX(calc(-' + (currentIndex * 100) + '% + ' + pointerDeltaX + 'px))';
    });

    function endPointer() {
      if (!isPointerDown) return;
      isPointerDown = false;
      track.style.transition = '';

      var threshold = track.clientWidth * 0.15;
      if (pointerDeltaX > threshold) {
        prev();
      } else if (pointerDeltaX < -threshold) {
        next();
      } else {
        goTo(currentIndex);
      }
      restartAutoplay();
    }

    track.addEventListener('pointerup', endPointer);
    track.addEventListener('pointerleave', function () {
      if (isPointerDown) endPointer();
    });
    track.addEventListener('pointercancel', endPointer);

    // Keyboard navigation
    root.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') {
        prev();
        restartAutoplay();
      } else if (e.key === 'ArrowRight') {
        next();
        restartAutoplay();
      }
    });

    // Recalculate on resize (in case of orientation change)
    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        goTo(currentIndex, true);
      }, 150);
    });

    // Init
    goTo(0, true);
    startAutoplay();

    // Pause autoplay when the section scrolls out of view (perf + UX)
    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              startAutoplay();
            } else {
              stopAutoplay();
            }
          });
        },
        { threshold: 0.25 }
      );
      observer.observe(root);
    }
  }

  function initAll() {
    var sliders = document.querySelectorAll('.image-slider-section');
    sliders.forEach(initSlider);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-init when a slider section is added/edited in the theme editor
  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target.querySelector('.image-slider-section');
    if (section) initSlider(section);
  });
})();